import { toast, ToastOptions, ToastContent, TypeOptions } from 'react-toastify';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface ToastConfig {
  title?: string;
  message: string;
  type: NotificationType;
  duration?: number;
  options?: Partial<ToastOptions>;
}

/**
 * Unified toast notification service for the admin panel
 * Provides consistent error handling and user feedback across all API calls
 */
export class ToastService {
  private static readonly DEFAULT_DURATION = 5000;
  private static readonly ERROR_DURATION = 8000;

  /**
   * Show a success notification
   */
  static success(message: string, title?: string, duration?: number): void {
    this.show({
      type: 'success',
      message,
      title,
      duration: duration || this.DEFAULT_DURATION,
    });
  }

  /**
   * Show an error notification
   */
  static error(message: string, title?: string, duration?: number): void {
    this.show({
      type: 'error',
      message,
      title,
      duration: duration || this.ERROR_DURATION,
    });
  }

  /**
   * Show a warning notification
   */
  static warning(message: string, title?: string, duration?: number): void {
    this.show({
      type: 'warning',
      message,
      title,
      duration: duration || this.DEFAULT_DURATION,
    });
  }

  /**
   * Show an info notification
   */
  static info(message: string, title?: string, duration?: number): void {
    this.show({
      type: 'info',
      message,
      title,
      duration: duration || this.DEFAULT_DURATION,
    });
  }

  /**
   * Handle API errors with proper error messages and console logging
   */
  static handleApiError(error: unknown, context: string): void {
    // Always log to console for developers
    console.error(`API Error in ${context}:`, error);

    let userMessage = 'An unexpected error occurred. Please try again.';
    let title = 'Error';

    if (error instanceof Error) {
      // Check if it's an API error with a response
      if ('response' in error && error.response) {
        const response = error.response as {
          data?: { message?: string };
          statusText?: string;
          status?: number;
        };
        if (response.data?.message) {
          userMessage = response.data.message;
        } else if (response.statusText) {
          userMessage = `Server error: ${response.statusText}`;
        }

        if (response.status === 401) {
          title = 'Authentication Error';
          userMessage = 'Please log in again to continue.';
        } else if (response.status === 403) {
          title = 'Permission Error';
          userMessage = 'You do not have permission to perform this action.';
        } else if (response.status === 404) {
          title = 'Not Found';
          userMessage = 'The requested resource was not found.';
        } else if (response.status && response.status >= 500) {
          title = 'Server Error';
          userMessage = 'Server error occurred. Please try again later.';
        }
      } else {
        // Standard error with message
        userMessage = error.message;
      }
    } else if (typeof error === 'string') {
      userMessage = error;
    }

    this.error(userMessage, title);
  }

  /**
   * Handle successful API operations
   */
  static handleApiSuccess(message: string, title?: string): void {
    this.success(message, title);
  }

  /**
   * Private method to show toast notifications
   */
  private static show({ type, message, title, duration, options = {} }: ToastConfig): void {
    // Create content with title if provided
    let content: ToastContent = message;
    if (title) {
      content = `${title}: ${message}`;
    }

    const toastOptions: ToastOptions = {
      type: type as TypeOptions,
      autoClose: duration,
      position: 'top-right',
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options,
    };

    toast(content, toastOptions);
  }
}

// Convenience exports for backward compatibility
export const showSuccessToast = ToastService.success.bind(ToastService);
export const showErrorToast = ToastService.error.bind(ToastService);
export const showWarningToast = ToastService.warning.bind(ToastService);
export const showInfoToast = ToastService.info.bind(ToastService);
export const handleApiError = ToastService.handleApiError.bind(ToastService);
export const handleApiSuccess = ToastService.handleApiSuccess.bind(ToastService);
